import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import { ShoppingBag, ExternalLink, Loader2, PlayCircle, Heart, Instagram, Youtube, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SHOPIFY_COLLECTION_URL = 'https://shop.finestfeathers.com/collections/zens-tshirts';

// Asset URLs
const HERO_IMAGE_URL = 'https://customer-assets.emergentagent.com/job_1373bd47-7a24-47ca-adae-0f2fe2678f07/artifacts/r299g3r0_IMG_7684.jpg';
const OWNER_PORTRAIT_URL = 'https://customer-assets.emergentagent.com/job_1373bd47-7a24-47ca-adae-0f2fe2678f07/artifacts/cvc9ibja_Untitled%20design%20%281%29.jpg';
const LOGO_IMAGE_URL = 'https://customer-assets.emergentagent.com/job_1373bd47-7a24-47ca-adae-0f2fe2678f07/artifacts/6d86gthk_Untitled%20design.jpg';
// Videos
const VIDEO_URL_1 = 'https://customer-assets.emergentagent.com/job_1373bd47-7a24-47ca-adae-0f2fe2678f07/artifacts/07mavhtl_215BFB46-39C4-4123-9253-EB9484920774.MOV';
const VIDEO_URL_2 = 'https://customer-assets.emergentagent.com/job_1373bd47-7a24-47ca-adae-0f2fe2678f07/artifacts/3ap6t4h4_copy_AD5CBB5B-0AC0-4F3E-A559-CB50C892F6AE.MOV';

export default function ZenTshirts() {
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/shopify/collections/zens-tshirts/products`);
        if (!cancelled) setProducts(res.data?.products || []);
      } catch (err) {
        console.error('Failed to load Zen T-Shirts collection:', err);
      } finally {
        if (!cancelled) setLoadingProducts(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Shared Header Component */}
      <Header />

      {/* HERO */}
      <section className="relative h-[80vh] min-h-[560px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={HERO_IMAGE_URL} alt="T-Shirts by Zeniyah Mae" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black"></div>
          {/* Floating orange orbs */}
          <div className="absolute -top-20 -left-20 w-[400px] h-[400px] rounded-full bg-orange-500/15 blur-3xl en-float-up"></div>
          <div className="absolute -bottom-20 -right-20 w-[500px] h-[500px] rounded-full bg-amber-500/12 blur-3xl en-float-down"></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl">
          <h1
            className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl mb-6 tracking-wider leading-[1] font-black text-white heading-oswald heading-glow break-words"
            style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '0.04em' }}
          >
            T-SHIRTS BY <span className="en-gradient-text">ZENIYAH MAE</span>
          </h1>
          <p className="text-base sm:text-xl md:text-2xl text-gray-200 mb-10 font-semibold tracking-wide max-w-2xl mx-auto">
            The Youngest Dreamers Become Tomorrow's Leaders
          </p>

          <div className="flex justify-center">
            <Button
              onClick={() => window.open(SHOPIFY_COLLECTION_URL, '_blank')}
              className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-8 py-6 text-base sm:text-lg rounded-full flex items-center gap-2 btn-pulse-glow"
              data-testid="zen-hero-shop-btn"
            >
              <ShoppingBag className="w-5 h-5" />
              Shop Zen's T-Shirts
            </Button>
          </div>
        </div>
      </section>

      {/* ABOUT THE OWNER */}
      <section className="py-20 sm:py-24 px-4 sm:px-6 bg-zinc-950 ambient-orange-bg">
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 heading-oswald heading-glow">
              <span className="text-orange-500">MEET</span> THE OWNER
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-center">
            {/* Portrait */}
            <div className="relative group order-2 md:order-1">
              <div className="absolute -inset-3 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 rounded-2xl opacity-40 blur-2xl group-hover:opacity-60 transition-opacity duration-500"></div>
              <img
                src={OWNER_PORTRAIT_URL}
                alt="Zeniyah Mae Lopez"
                className="relative w-full rounded-2xl shadow-2xl border-2 border-orange-500/30 object-cover h-[420px] sm:h-[500px]"
                data-testid="zen-owner-portrait"
              />
              {/* Floating badge */}
              <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-orange-500 text-black text-xs font-black uppercase tracking-wider px-5 py-2 rounded-full shadow-lg btn-pulse-glow whitespace-nowrap flex items-center gap-2">
                <Heart className="w-4 h-4" />
                Founder · Creator
              </div>
            </div>

            {/* Bio */}
            <div className="order-1 md:order-2 text-center md:text-left">
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-2 heading-oswald" style={{ fontFamily: 'Oswald, sans-serif' }}>
                <span className="en-gradient-text">ZENIYAH MAE</span> LOPEZ
              </h3>
              <p className="text-orange-400 text-base sm:text-lg font-semibold mb-2">
                Founder · Designer · Age 4
              </p>
              <p className="text-gray-400 text-sm sm:text-base mb-6 italic">
                Pomo/Karuk/Pit River
              </p>

              <div className="space-y-4 text-gray-300 text-base sm:text-lg leading-relaxed">
                <p>
                  Zeniyah Mae Lopez is a proud <span className="text-orange-400 font-semibold">4-year-old Pomo entrepreneur</span> who loves creating T-shirts alongside her daddy. Known for her colorful imagination and love of hearts, rainbows, and drawing, she reflects the joy and kindness she shares with the world.
                </p>
                <p>
                  Family means everything to Zeniyah, and she is already breaking barriers by becoming one of the youngest business owners in her family. Through her creativity and determination, she hopes to inspire other young people to take healthy risks, believe in themselves, and chase their dreams, no matter how young they are.
                </p>
                <p className="italic text-gray-400">
                  <span className="text-orange-400">&ldquo;</span>
                  You&rsquo;re never too young to chase your dreams.
                  <span className="text-orange-400">&rdquo;</span>
                </p>
                <p className="text-orange-400 font-bold text-lg sm:text-xl mt-4">
                  From Crayons to CEO
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MY FIRST LOGO */}
      <section className="py-20 sm:py-24 px-4 sm:px-6 bg-black ambient-orange-bg">
        <div className="container mx-auto max-w-4xl relative z-10">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 heading-oswald heading-glow">
              <span className="text-orange-500">MY FIRST</span> LOGO
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto"></div>
          </div>

          <div className="relative group mx-auto max-w-md">
            <div className="absolute -inset-4 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 rounded-3xl opacity-30 blur-2xl group-hover:opacity-50 transition-opacity duration-500"></div>
            <div className="relative bg-zinc-950 border-2 border-orange-500/40 rounded-3xl p-8 shadow-2xl">
              <img
                src={LOGO_IMAGE_URL}
                alt="My First Logo - Heart People"
                className="w-full h-auto rounded-xl"
                data-testid="zen-first-logo"
              />
            </div>
          </div>
        </div>
      </section>

      {/* COLLECTION */}
      <section id="zen-collection" className="py-20 sm:py-24 px-4 sm:px-6 bg-zinc-900 ambient-orange-bg">
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="text-center mb-12">
            <p className="text-orange-500 text-xs sm:text-sm font-bold uppercase tracking-[0.3em] mb-3">Shop the Drop</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 heading-oswald heading-glow">
              <span className="text-orange-500">ZEN</span> COLLECTION
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto mb-6"></div>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Every shirt tells a story — explore the full lineup
            </p>
          </div>

          {loadingProducts ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-12 h-12 text-orange-500 animate-spin" />
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-400 mb-6">Collection is loading or temporarily unavailable. Visit our Shopify store directly:</p>
              <Button
                onClick={() => window.open(SHOPIFY_COLLECTION_URL, '_blank')}
                className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-8 py-5 rounded-full btn-pulse-glow"
              >
                Visit Zen T-Shirts Collection
                <ExternalLink className="w-5 h-5 ml-2" />
              </Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {products.map((p) => (
                  <a
                    key={p.id}
                    href={p.shopify_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group block bg-zinc-950 border border-zinc-800 hover:border-orange-500/70 rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/20"
                    data-testid={`zen-product-${p.id}`}
                  >
                    <div className="relative aspect-square bg-zinc-800 overflow-hidden">
                      {p.image_url ? (
                        <img
                          src={p.image_url}
                          alt={p.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-600">
                          <ShoppingBag className="w-12 h-12" />
                        </div>
                      )}
                      <div className="absolute top-3 left-3 bg-orange-500 text-black text-[10px] sm:text-xs font-black uppercase tracking-wider px-2.5 py-1 rounded-full">
                        Zen
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-white font-bold text-sm sm:text-base mb-1 line-clamp-2 leading-tight group-hover:text-orange-400 transition-colors">
                        {p.name}
                      </h3>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-orange-400 font-black text-lg sm:text-xl">{p.price}</span>
                        <ExternalLink className="w-4 h-4 text-gray-500 group-hover:text-orange-400 transition-colors" />
                      </div>
                    </div>
                  </a>
                ))}
              </div>

              <div className="text-center mt-12">
                <Button
                  onClick={() => window.open(SHOPIFY_COLLECTION_URL, '_blank')}
                  className="bg-orange-500 hover:bg-orange-600 text-black font-bold px-10 py-6 text-lg rounded-full btn-pulse-glow"
                  data-testid="zen-shop-full-collection-btn"
                >
                  Shop Full Collection on Shopify
                  <ExternalLink className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* VIDEO SECTION */}
      <section className="py-20 sm:py-24 px-4 sm:px-6 bg-zinc-950 ambient-orange-bg">
        <div className="container mx-auto max-w-5xl relative z-10">
          <div className="text-center mb-10">
            <p className="text-orange-500 text-xs sm:text-sm font-bold uppercase tracking-[0.3em] mb-3">Watch the Story</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 heading-oswald heading-glow">
              <span className="text-orange-500">ZEN</span> ON FILM
            </h2>
            <div className="w-24 h-1 bg-orange-500 mx-auto"></div>
          </div>

          {/* Video 1 - New Video */}
          <div className="relative mb-12">
            <div className="absolute -inset-3 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 rounded-2xl opacity-40 blur-2xl en-pulse-glow"></div>
            <div className="relative aspect-video bg-zinc-950 border-2 border-orange-500/50 rounded-2xl overflow-hidden">
              <video
                src={VIDEO_URL_1}
                controls
                preload="metadata"
                className="w-full h-full object-contain bg-black"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>

          {/* Video 2 - Zen makes T-shirt */}
          <div className="text-center mb-6">
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white heading-oswald">
              Zen Makes T-Shirt in Less Than 1 Minute
            </h3>
          </div>
          <div className="relative">
            <div className="absolute -inset-3 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-500 rounded-2xl opacity-30 blur-2xl"></div>
            <div className="relative aspect-video bg-zinc-950 border-2 border-orange-500/50 rounded-2xl overflow-hidden">
              <video
                src={VIDEO_URL_2}
                controls
                preload="metadata"
                className="w-full h-full object-contain bg-black"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-12 px-4 sm:px-6 border-t border-zinc-800">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <Link to="/" className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_style-central-65/artifacts/n560cpi8_Untitled%20design%20%2820%29.png"
                alt="Finest Feathers Logo"
                className="h-10 w-auto"
              />
              <span className="text-lg font-bold text-white heading-oswald" style={{ fontFamily: 'Oswald, sans-serif', letterSpacing: '0.14em' }}>
                FINEST FEATHERS
              </span>
            </Link>
            <div className="flex gap-6">
              <a href="https://www.instagram.com/finestfeathers/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-orange-500 transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.youtube.com/@finestfeathers" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-orange-500 transition-colors">
                <Youtube className="w-5 h-5" />
              </a>
              <a href="mailto:hello@finestfeathers.com" className="text-gray-400 hover:text-orange-500 transition-colors">
                <Mail className="w-5 h-5" />
              </a>
            </div>
            <p className="text-gray-500 text-sm">© 2026 Finest Feathers · Zen T-Shirts</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
